import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { CATEGORIES_PIECE, personName } from './constants';

const BLANK = {
  site_id: '', localisation_precise: '', activite_id: '', titre: '', reference_interne: '',
  description: '', note_interne: '', date_souhaitee: '', date_possible_debut: '', date_limite: '',
  astreinte: false, urgence: false, prestataire_id: '', responsable_id: '',
};

export default function DemandeForm({ currentUser, refs, demandeId, onCancel, onSaved }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super-admin';
  const isEdit = !!demandeId;

  const [form, setForm] = useState(BLANK);
  const [staged, setStaged] = useState([]); // fichiers en attente d'upload (création)
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cat, setCat] = useState('Photo');
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/maintenance/demandes/${demandeId}`);
        setForm({
          site_id: data.site_id || '', localisation_precise: data.localisation_precise || '',
          activite_id: data.activite_id || '', titre: data.titre || '', reference_interne: data.reference_interne || '',
          description: data.description || '', note_interne: data.note_interne || '',
          date_souhaitee: data.date_souhaitee ? data.date_souhaitee.slice(0, 10) : '',
          date_possible_debut: data.date_possible_debut ? data.date_possible_debut.slice(0, 10) : '',
          date_limite: data.date_limite ? data.date_limite.slice(0, 10) : '',
          astreinte: !!data.astreinte, urgence: !!data.urgence,
          prestataire_id: data.prestataire_id || '', responsable_id: data.responsable_id || '',
        });
      } catch {
        setError('Demande introuvable ou accès refusé.');
      } finally {
        setLoading(false);
      }
    })();
  }, [demandeId, isEdit]);

  const addFiles = useCallback((fileList) => {
    const arr = Array.from(fileList).map((file) => ({ file, categorie: cat }));
    setStaged((s) => [...s, ...arr]);
  }, [cat]);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  const uploadStaged = async (id) => {
    for (const item of staged) {
      const fd = new FormData();
      fd.append('fichier', item.file);
      fd.append('categorie', item.categorie);
      try { await api.post(`/maintenance/demandes/${id}/pieces`, fd); } catch { /* on continue */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.site_id) return setError('Le site est requis.');
    if (!form.activite_id) return setError("L'activité est requise.");
    if (!form.titre.trim()) return setError('Le titre est requis.');
    setSaving(true);
    try {
      let id = demandeId;
      if (isEdit) {
        await api.put(`/maintenance/demandes/${demandeId}`, form);
      } else {
        const { data } = await api.post('/maintenance/demandes', form);
        id = data.id;
      }
      if (staged.length) await uploadStaged(id);
      onSaved(id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement.');
      setSaving(false);
    }
  };

  if (loading) return <div className="table-empty">Chargement…</div>;

  return (
    <form onSubmit={submit}>
      <div className="page-header-row">
        <div>
          <button type="button" className="mnt-back" onClick={onCancel}>← Liste des demandes</button>
          <h2 className="page-title" style={{ marginTop: 6 }}>{isEdit ? 'Modifier la demande' : 'Nouvelle demande d\'intervention'}</h2>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>Annuler</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="mnt-grid-2">
        {/* ─── Colonne gauche ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
          <div className="mnt-card">
            <h3>Localisation</h3>
            <div className="form-group">
              <label>Site <span className="mnt-req">*</span></label>
              <select className="form-control" value={form.site_id} onChange={(e) => set({ site_id: e.target.value })}>
                <option value="">— Sélectionner —</option>
                {refs.sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Localisation précise</label>
              <input className="form-control" placeholder="ex. vestiaire H, réserve, accueil…" value={form.localisation_precise} onChange={(e) => set({ localisation_precise: e.target.value })} />
            </div>
          </div>

          <div className="mnt-card">
            <h3>Détails de l'intervention</h3>
            <div className="form-group">
              <label>Activité / Lot <span className="mnt-req">*</span></label>
              <select className="form-control" value={form.activite_id} onChange={(e) => set({ activite_id: e.target.value })}>
                <option value="">— Sélectionner —</option>
                {refs.activites.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
            <div className="mnt-row">
              <div className="form-group" style={{ flex: '2 1 200px' }}>
                <label>Titre <span className="mnt-req">*</span></label>
                <input className="form-control" value={form.titre} onChange={(e) => set({ titre: e.target.value })} autoFocus />
              </div>
              <div className="form-group" style={{ flex: '1 1 140px' }}>
                <label>Référence interne</label>
                <input className="form-control" value={form.reference_interne} onChange={(e) => set({ reference_interne: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-control mnt-textarea" placeholder="Décrivez le problème constaté…" value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Note interne</label>
              <textarea className="form-control mnt-textarea" value={form.note_interne} onChange={(e) => set({ note_interne: e.target.value })} />
            </div>
          </div>
        </div>

        {/* ─── Colonne droite ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--section-gap)' }}>
          <div className="mnt-card">
            <h3>Planification</h3>
            <div className="mnt-row">
              <div className="form-group">
                <label>Date souhaitée</label>
                <input type="date" className="form-control" value={form.date_souhaitee} onChange={(e) => set({ date_souhaitee: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Possible à partir du</label>
                <input type="date" className="form-control" value={form.date_possible_debut} onChange={(e) => set({ date_possible_debut: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>À terminer au plus tard le</label>
                <input type="date" className="form-control" value={form.date_limite} onChange={(e) => set({ date_limite: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="mnt-card">
            <h3>Spécificités</h3>
            <div className="mnt-row">
              <div>
                <label className="form-group" style={{ marginBottom: 6 }}>Astreinte</label>
                <div className="mnt-toggle">
                  <button type="button" className={form.astreinte ? 'active' : ''} onClick={() => set({ astreinte: true })}>Oui</button>
                  <button type="button" className={!form.astreinte ? 'active' : ''} onClick={() => set({ astreinte: false })}>Non</button>
                </div>
              </div>
              <div>
                <label className="form-group" style={{ marginBottom: 6 }}>Urgence</label>
                <div className="mnt-toggle">
                  <button type="button" className={form.urgence ? 'active danger' : ''} onClick={() => set({ urgence: true })}>Oui</button>
                  <button type="button" className={!form.urgence ? 'active' : ''} onClick={() => set({ urgence: false })}>Non</button>
                </div>
              </div>
            </div>
          </div>

          <div className="mnt-card">
            <h3>Documents (photos / PDF)</h3>
            <div className="form-group">
              <label>Catégorie</label>
              <select className="form-control" value={cat} onChange={(e) => setCat(e.target.value)}>
                {CATEGORIES_PIECE.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div
              className={`mnt-drop ${drag ? 'drag' : ''}`}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
            >
              Glissez-déposez vos fichiers ou <strong>parcourir</strong>
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="mnt-hidden-input"
              onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }} />
            {staged.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {staged.map((s, i) => (
                  <div key={i} className="mnt-file-row">
                    <span>📎 {s.file.name} <span className="mnt-muted">· {s.categorie}</span></span>
                    <button type="button" className="btn-icon delete" onClick={() => setStaged((arr) => arr.filter((_, j) => j !== i))}>🗑️</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isAdmin && (
            <div className="mnt-card">
              <h3>Affectation</h3>
              <div className="form-group">
                <label>Prestataire</label>
                <select className="form-control" value={form.prestataire_id} onChange={(e) => set({ prestataire_id: e.target.value })}>
                  <option value="">— Aucun —</option>
                  {refs.prestataires.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Responsable maintenance</label>
                <select className="form-control" value={form.responsable_id} onChange={(e) => set({ responsable_id: e.target.value })}>
                  <option value="">— Aucun —</option>
                  {refs.users.map((u) => <option key={u.id} value={u.id}>{personName(u)}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
